import * as React from 'react';
import { ReactSortable } from 'react-sortablejs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  FormCondition,
  FormConditionOperator,
  FormDefinition,
  FormElement,
  FormElementType,
  FormLogicRule,
  FormMediaRef,
  FormOption
} from '../../types/formSchema';
import { FORM_FIELD_TYPE_REGISTRY } from '../../helpers/form/formFieldRegistry';
import {
  removeElementAndReferences,
  validateFormDefinitionStructure
} from '../../helpers/form/formDefinitionValidation';
import { FORM_START_PAGE_ID, buildFormPages } from '../../helpers/form/formFlowRuntime';
import { buildFormMediaUrl, normalizeFormMediaUrl, resolveLegacyFormImageUrl } from '../../helpers/form/formMedia';
import { getFormThemeColors } from '../../helpers/form/formTheme';
import './FormBuilderV2.scss';

const Sortable = ReactSortable as any;

interface Props {
  definition: FormDefinition;
  onChange: (definition: FormDefinition) => void;
  uploadImage?: (file: File) => Promise<string | FormMediaRef>;
  uploadFile?: (file: File) => Promise<FormMediaRef>;
  mode?: 'COMPACT' | 'FULL';
  formHeaderExtra?: React.ReactNode;
}

const QUESTION_PALETTE_TYPES: FormElementType[] = [
  'SHORT_TEXT',
  'LONG_TEXT',
  'NUMBER',
  'DATE',
  'DATE_TIME',
  'SINGLE_CHOICE',
  'MULTIPLE_CHOICE',
  'YES_NO',
  'EMAIL',
  'PHONE',
  'RATING',
  'ACKNOWLEDGEMENT'
];

const CONTENT_PALETTE_TYPES: FormElementType[] = [
  'HEADING',
  'PARAGRAPH',
  'URL',
  'IMAGE',
  'ATTACHMENT',
  'DIVIDER',
  'SECTION',
  'PAGE_BREAK'
];

const PALETTE_GROUPS: Array<{ label: string; description: string; types: FormElementType[] }> = [
  {
    label: 'Answer fields',
    description: 'Questions recipients answer.',
    types: QUESTION_PALETTE_TYPES
  },
  {
    label: 'Content blocks',
    description: 'Instructions, layout, images and downloadable files.',
    types: CONTENT_PALETTE_TYPES
  }
];

const EMPTY_OPERATORS: FormConditionOperator[] = ['EMPTY', 'NOT_EMPTY'];
const TEXT_OPERATORS: FormConditionOperator[] = ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'EMPTY', 'NOT_EMPTY'];
const CHOICE_OPERATORS: FormConditionOperator[] = ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'EMPTY', 'NOT_EMPTY'];
const NUMBER_OPERATORS: FormConditionOperator[] = [
  'EQUALS',
  'NOT_EQUALS',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL',
  'EMPTY',
  'NOT_EMPTY'
];
const DATE_OPERATORS: FormConditionOperator[] = ['EQUALS', 'NOT_EQUALS', 'BEFORE', 'AFTER', 'EMPTY', 'NOT_EMPTY'];
const EMOJI_PICKER_OPTIONS = [
  'ℹ️',
  '⚠️',
  '✅',
  '📌',
  '📅',
  '📍',
  '🚌',
  '🏫',
  '🏆',
  '⚽',
  '🎾',
  '🏊',
  '🍽️',
  '💊',
  '📝',
  '📎',
  '⭐',
  '❤️'
];

const OPERATOR_LABELS: Record<FormConditionOperator, string> = {
  EQUALS: 'Equals',
  NOT_EQUALS: 'Does not equal',
  CONTAINS: 'Contains',
  IN: 'Is one of',
  EMPTY: 'Is empty',
  NOT_EMPTY: 'Is not empty',
  GREATER_THAN: 'Is greater than',
  GREATER_THAN_OR_EQUAL: 'Is at least',
  LESS_THAN: 'Is less than',
  LESS_THAN_OR_EQUAL: 'Is at most',
  BEFORE: 'Is before',
  AFTER: 'Is after'
};

const operatorsForElement = (element?: FormElement): FormConditionOperator[] => {
  if (!element) return EMPTY_OPERATORS;
  if (['NUMBER', 'RATING'].includes(element.type)) return NUMBER_OPERATORS;
  if (['DATE', 'DATE_TIME'].includes(element.type)) return DATE_OPERATORS;
  if (['SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'YES_NO', 'ACKNOWLEDGEMENT'].includes(element.type)) {
    return CHOICE_OPERATORS;
  }
  return TEXT_OPERATORS;
};

const defaultConditionValue = (element?: FormElement): unknown => {
  if (!element) return '';
  if (element.type === 'YES_NO' || element.type === 'ACKNOWLEDGEMENT') return true;
  if (element.type === 'SINGLE_CHOICE' || element.type === 'MULTIPLE_CHOICE') {
    return element.config?.options?.[0]?.value || (element.config?.allowOther ? '__other__' : '');
  }
  if (element.type === 'NUMBER' || element.type === 'RATING') return 0;
  return '';
};

const makeId = (prefix: string): string =>
  `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const isDirectMediaUrl = (value: string): boolean => /^(https?:|data:|blob:|\/)/.test(value);

const mediaUrlFromConfig = (element: FormElement): string => {
  const mediaUrl = normalizeFormMediaUrl(String(element.config?.mediaUrl || ''));
  if (mediaUrl) return resolveLegacyFormImageUrl(mediaUrl);
  const signedUrl = buildFormMediaUrl(
    String(element.config?.mediaKey || element.config?.mediaId || ''),
    element.config?.mediaToken
  );
  if (signedUrl) return signedUrl;
  const mediaId = normalizeFormMediaUrl(String(element.config?.mediaId || element.config?.mediaKey || ''));
  return resolveLegacyFormImageUrl(mediaId);
};

const mediaConfigFromUpload = (uploaded: FormMediaRef): Record<string, unknown> => {
  const common = {
    mediaUrl: uploaded.url,
    mediaName: uploaded.name,
    mediaSize: uploaded.size,
    mediaMimeType: uploaded.mimeType
  };

  if (!uploaded.token) {
    return {
      ...common,
      mediaId: undefined,
      mediaKey: undefined,
      mediaToken: undefined
    };
  }

  return {
    ...common,
    mediaId: uploaded.id,
    mediaKey: uploaded.id,
    mediaToken: uploaded.token
  };
};
const makeOption = (label: string): FormOption => ({
  id: makeId('option'),
  label,
  value: makeId('value')
});

const createElement = (type: FormElementType): FormElement => {
  const typeDefinition = FORM_FIELD_TYPE_REGISTRY[type];
  const element: FormElement = {
    id: makeId('field'),
    kind: typeDefinition.kind,
    type,
    title: type === 'PARAGRAPH' ? '' : typeDefinition.kind === 'QUESTION' ? 'Untitled question' : typeDefinition.label,
    required: false
  };

  if (typeDefinition.supportsOptions) {
    element.config = {
      options: [makeOption('Option 1'), makeOption('Option 2')],
      allowOther: false,
      otherLabel: 'Other',
      otherRequired: false
    };
  }

  if (type === 'RATING') {
    element.config = { min: 1, max: 5, step: 1 };
  }

  if (type === 'FILE_UPLOAD') {
    element.config = {
      maxFiles: 1,
      maxFileSizeBytes: 10 * 1024 * 1024,
      allowedMimeTypes: []
    };
  }

  return element;
};

export const FormBuilderV2: React.FC<Props> = ({
  definition,
  onChange,
  uploadImage,
  uploadFile,
  mode = 'FULL',
  formHeaderExtra
}) => {
  const [selectedId, setSelectedId] = React.useState<string>(definition.elements[0]?.id || '');
  const [settingsTab, setSettingsTab] = React.useState<'ELEMENT' | 'APPEARANCE'>('ELEMENT');
  const [mediaError, setMediaError] = React.useState('');
  const [isUploadingMedia, setIsUploadingMedia] = React.useState(false);
  const [localImagePreviews, setLocalImagePreviews] = React.useState<Record<string, string>>({});
  const [emojiTarget, setEmojiTarget] = React.useState<'title' | 'description'>('title');
  const canvasRef = React.useRef<HTMLElement | null>(null);
  const titleInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const descriptionInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const elementRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const selected = definition.elements.find(element => element.id === selectedId);
  const themeColors = getFormThemeColors(definition.theme);
  const structureIssues = validateFormDefinitionStructure(definition);
  const selectedIssues = structureIssues.filter(issue => !issue.elementId || issue.elementId === selectedId);
  const isCompact = mode === 'COMPACT';
  const sourceQuestions = definition.elements.filter(
    element =>
      element.kind === 'QUESTION' &&
      element.id !== selectedId &&
      FORM_FIELD_TYPE_REGISTRY[element.type].canDriveConditions
  );
  const pages = buildFormPages(definition);
  const pageIndexById = new Map(pages.map((page, index) => [page.id, index]));
  const elementPageId = new Map<string, string>();
  pages.forEach(page => page.elements.forEach(element => elementPageId.set(element.id, page.id)));
  const questionsForSourcePage = (pageId: string) =>
    definition.elements.filter(element => {
      if (element.kind !== 'QUESTION' || !FORM_FIELD_TYPE_REGISTRY[element.type].canDriveConditions) return false;
      const questionPageId = elementPageId.get(element.id) || FORM_START_PAGE_ID;
      return (pageIndexById.get(questionPageId) || 0) <= (pageIndexById.get(pageId) || 0);
    });
  const targetsAfterPage = (pageId: string) =>
    pages.filter(page => (pageIndexById.get(page.id) || 0) > (pageIndexById.get(pageId) || 0));

  React.useEffect(() => {
    if (selectedId && definition.elements.some(element => element.id === selectedId)) return;
    setSelectedId(definition.elements[0]?.id || '');
  }, [definition.elements, selectedId]);

  const setElements = (elements: FormElement[]) => onChange({ ...definition, elements });

  const scrollElementIntoView = (elementId: string) => {
    let attempts = 0;
    const scroll = () => {
      const canvas = canvasRef.current;
      const element = elementRefs.current[elementId];
      if (!canvas || !element) {
        attempts += 1;
        if (attempts < 3) window.requestAnimationFrame(scroll);
        return;
      }

      const canvasRect = canvas.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      if (elementRect.top < canvasRect.top || elementRect.bottom > canvasRect.bottom) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    window.requestAnimationFrame(scroll);
  };

  const addElement = (type: FormElementType) => {
    const element = createElement(type);
    setElements([...definition.elements, element]);
    setSelectedId(element.id);
    setMediaError('');
    scrollElementIntoView(element.id);
  };

  const updateSelected = (patch: Partial<FormElement>) => {
    if (!selected) return;
    setElements(definition.elements.map(element => (element.id === selected.id ? { ...element, ...patch } : element)));
  };

  const updateConfig = (patch: Record<string, unknown>) => {
    if (!selected) return;
    updateSelected({ config: { ...(selected.config || {}), ...patch } });
  };

  const updateTheme = (patch: NonNullable<FormDefinition['theme']>) => {
    onChange({
      ...definition,
      theme: {
        ...(definition.theme || {}),
        ...patch
      }
    });
  };

  const insertEmoji = (emoji: string) => {
    if (!selected) return;
    const field =
      emojiTarget === 'description' && !['DIVIDER', 'IMAGE'].includes(selected.type) ? 'description' : 'title';
    const control = field === 'description' ? descriptionInputRef.current : titleInputRef.current;
    const current = String(selected[field] || '');
    const start = control?.selectionStart ?? current.length;
    const end = control?.selectionEnd ?? start;
    const next = `${current.slice(0, start)}${emoji}${current.slice(end)}`;
    updateSelected({ [field]: next } as Partial<FormElement>);
    window.requestAnimationFrame(() => {
      control?.focus();
      const cursor = start + emoji.length;
      control?.setSelectionRange(cursor, cursor);
    });
  };
  const selectElement = (elementId: string) => {
    setSelectedId(elementId);
    setSettingsTab('ELEMENT');
    setMediaError('');
    scrollElementIntoView(elementId);
  };

  const removeElement = (elementId: string) => {
    const index = definition.elements.findIndex(element => element.id === elementId);
    if (index < 0) return;
    const nextDefinition = removeElementAndReferences(definition, elementId);
    onChange(nextDefinition);
    const next = nextDefinition.elements;
    setSelectedId(next[Math.min(index, Math.max(0, next.length - 1))]?.id || '');
  };

  const moveElement = (elementId: string, direction: -1 | 1) => {
    const index = definition.elements.findIndex(element => element.id === elementId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= definition.elements.length) return;
    const next = [...definition.elements];
    const [element] = next.splice(index, 1);
    next.splice(targetIndex, 0, element);
    setElements(next);
    setSelectedId(elementId);
  };

  const duplicateElement = (elementId: string) => {
    const source = definition.elements.find(element => element.id === elementId);
    if (!source) return;
    const duplicate: FormElement = {
      ...source,
      id: makeId('field'),
      config: source.config
        ? {
            ...source.config,
            options: source.config.options?.map(option => ({ ...option, id: makeId('option') }))
          }
        : undefined
    };
    const index = definition.elements.findIndex(element => element.id === elementId);
    const next = [...definition.elements];
    next.splice(index + 1, 0, duplicate);
    setElements(next);
    selectElement(duplicate.id);
  };

  const updateOption = (optionId: string, label: string) => {
    const options = (selected?.config?.options || []).map(option =>
      option.id === optionId ? { ...option, label } : option
    );
    updateConfig({ options });
  };

  const removeOption = (optionId: string) => {
    updateConfig({ options: (selected?.config?.options || []).filter(option => option.id !== optionId) });
  };

  const setVisibilityEnabled = (enabled: boolean) => {
    if (!selected) return;
    if (!enabled) {
      const { visibility, visibilityEffect, ...rest } = selected;
      setElements(definition.elements.map(element => (element.id === selected.id ? (rest as FormElement) : element)));
      return;
    }

    const firstSource = sourceQuestions[0];
    if (!firstSource) return;
    updateSelected({
      visibilityEffect: 'SHOW',
      visibility: {
        match: 'ALL',
        conditions: [
          {
            fieldId: firstSource.id,
            operator: operatorsForElement(firstSource)[0],
            value: defaultConditionValue(firstSource)
          }
        ]
      }
    });
  };

  const updateCondition = (index: number, patch: Partial<FormCondition>) => {
    if (!selected?.visibility) return;
    const conditions = selected.visibility.conditions.map((condition, conditionIndex) =>
      conditionIndex === index ? { ...condition, ...patch } : condition
    );
    updateSelected({ visibility: { ...selected.visibility, conditions } });
  };

  const changeConditionSource = (index: number, fieldId: string) => {
    const source = definition.elements.find(element => element.id === fieldId);
    const operator = operatorsForElement(source)[0];
    updateCondition(index, {
      fieldId,
      operator,
      value: defaultConditionValue(source)
    });
  };

  const addCondition = () => {
    if (!selected?.visibility || sourceQuestions.length === 0) return;
    const source = sourceQuestions[0];
    updateSelected({
      visibility: {
        ...selected.visibility,
        conditions: [
          ...selected.visibility.conditions,
          {
            fieldId: source.id,
            operator: operatorsForElement(source)[0],
            value: defaultConditionValue(source)
          }
        ]
      }
    });
  };

  const removeCondition = (index: number) => {
    if (!selected?.visibility) return;
    const conditions = selected.visibility.conditions.filter((_, conditionIndex) => conditionIndex !== index);
    if (conditions.length === 0) {
      setVisibilityEnabled(false);
      return;
    }
    updateSelected({ visibility: { ...selected.visibility, conditions } });
  };

  const renderConditionValue = (
    condition: FormCondition,
    onConditionChange: (patch: Partial<FormCondition>) => void
  ) => {
    if (EMPTY_OPERATORS.includes(condition.operator)) return null;
    const source = definition.elements.find(element => element.id === condition.fieldId);
    if (!source) return <div className="FormBuilderV2__broken">Referenced question was deleted.</div>;

    if (source.type === 'YES_NO' || source.type === 'ACKNOWLEDGEMENT') {
      return (
        <select
          className="form-control"
          value={String(condition.value)}
          onChange={event => onConditionChange({ value: event.target.value === 'true' })}
        >
          <option value="true">Yes / checked</option>
          <option value="false">No / unchecked</option>
        </select>
      );
    }

    if (source.type === 'SINGLE_CHOICE' || source.type === 'MULTIPLE_CHOICE') {
      return (
        <select
          className="form-control"
          value={String(condition.value ?? '')}
          onChange={event => onConditionChange({ value: event.target.value })}
        >
          {(source.config?.options || []).map(option => (
            <option key={option.id} value={option.value}>
              {option.label}
            </option>
          ))}
          {source.config?.allowOther && <option value="__other__">{source.config.otherLabel || 'Other'}</option>}
        </select>
      );
    }

    const inputType = ['NUMBER', 'RATING'].includes(source.type)
      ? 'number'
      : source.type === 'DATE'
      ? 'date'
      : source.type === 'DATE_TIME'
      ? 'datetime-local'
      : 'text';

    return (
      <input
        className="form-control"
        type={inputType}
        value={(condition.value as any) ?? ''}
        onChange={event =>
          onConditionChange({
            value: inputType === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value
          })
        }
      />
    );
  };

  const updateLogicRule = (ruleId: string, patch: Partial<FormLogicRule>) => {
    onChange({
      ...definition,
      logic: (definition.logic || []).map(rule => (rule.id === ruleId ? { ...rule, ...patch } : rule))
    });
  };

  const updateLogicCondition = (ruleId: string, index: number, patch: Partial<FormCondition>) => {
    const rule = (definition.logic || []).find(item => item.id === ruleId);
    if (!rule) return;
    updateLogicRule(ruleId, {
      when: {
        ...rule.when,
        conditions: rule.when.conditions.map((condition, conditionIndex) =>
          conditionIndex === index ? { ...condition, ...patch } : condition
        )
      }
    });
  };

  const addLogicRule = () => {
    const source = sourceQuestions[0] || definition.elements.find(element => element.kind === 'QUESTION');
    if (!source) return;
    const firstTarget = pages.find(page => page.id !== FORM_START_PAGE_ID);
    const rule: FormLogicRule = {
      id: makeId('rule'),
      sourcePageId: FORM_START_PAGE_ID,
      when: {
        match: 'ALL',
        conditions: [
          {
            fieldId: source.id,
            operator: operatorsForElement(source)[0],
            value: defaultConditionValue(source)
          }
        ]
      },
      action: firstTarget ? { type: 'GO_TO_PAGE', targetId: firstTarget.id } : { type: 'SUBMIT' }
    };
    onChange({ ...definition, logic: [...(definition.logic || []), rule] });
  };

  const removeLogicRule = (ruleId: string) => {
    onChange({ ...definition, logic: (definition.logic || []).filter(rule => rule.id !== ruleId) });
  };

  const setLogicActionType = (rule: FormLogicRule, actionType: 'GO_TO_PAGE' | 'SUBMIT' | 'END_WITH_MESSAGE') => {
    if (actionType === 'GO_TO_PAGE') {
      const target = targetsAfterPage(rule.sourcePageId)[0];
      if (!target) {
        updateLogicRule(rule.id, { action: { type: 'SUBMIT' } });
        return;
      }
      updateLogicRule(rule.id, {
        action: { type: 'GO_TO_PAGE', targetId: target?.id || FORM_START_PAGE_ID }
      });
      return;
    }
    if (actionType === 'END_WITH_MESSAGE') {
      updateLogicRule(rule.id, {
        action: { type: 'END_WITH_MESSAGE', message: 'Thank you. Your response is complete.' }
      });
      return;
    }
    updateLogicRule(rule.id, { action: { type: 'SUBMIT' } });
  };

  const addLogicCondition = (rule: FormLogicRule) => {
    const source = questionsForSourcePage(rule.sourcePageId)[0];
    if (!source) return;
    updateLogicRule(rule.id, {
      when: {
        ...rule.when,
        conditions: [
          ...rule.when.conditions,
          {
            fieldId: source.id,
            operator: operatorsForElement(source)[0],
            value: defaultConditionValue(source)
          }
        ]
      }
    });
  };

  const removeLogicCondition = (rule: FormLogicRule, index: number) => {
    if (rule.when.conditions.length === 1) {
      removeLogicRule(rule.id);
      return;
    }
    updateLogicRule(rule.id, {
      when: {
        ...rule.when,
        conditions: rule.when.conditions.filter((_, conditionIndex) => conditionIndex !== index)
      }
    });
  };

  const renderElementSummary = (element: FormElement, index: number) => {
    const meta = FORM_FIELD_TYPE_REGISTRY[element.type];
    const optionCount = element.config?.options?.length || 0;
    const elementIssues = structureIssues.filter(issue => issue.elementId === element.id);
    const imageSrc = element.type === 'IMAGE' ? localImagePreviews[element.id] || mediaUrlFromConfig(element) : '';
    return (
      <div
        ref={node => {
          elementRefs.current[element.id] = node;
        }}
        className={`FormBuilderV2__element${selectedId === element.id ? ' FormBuilderV2__element--selected' : ''}${
          elementIssues.length ? ' FormBuilderV2__element--needsAttention' : ''
        }`}
        onClick={() => selectElement(element.id)}
        onKeyDown={event => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          selectElement(element.id);
        }}
        role="button"
        tabIndex={0}
        aria-pressed={selectedId === element.id}
        aria-label={`Edit ${element.title || meta.label}`}
      >
        <button type="button" className="FormBuilderV2__drag" aria-label="Drag to reorder">
          ⋮⋮
        </button>
        <div className="FormBuilderV2__elementMain">
          <div
            className={`FormBuilderV2__elementTitle${element.required ? ' FormBuilderV2__elementTitle--required' : ''}`}
          >
            {element.title || meta.label}
            {element.required && <span aria-hidden="true"> *</span>}
          </div>
          {element.description && <div className="FormBuilderV2__elementDescription">{element.description}</div>}
          {element.type === 'IMAGE' &&
            (imageSrc ? (
              <img className="FormBuilderV2__elementImage" src={imageSrc} alt={element.title || ''} />
            ) : (
              <div className="FormBuilderV2__inlineNotice">Choose an image file to show this block in the form.</div>
            ))}
          {element.type === 'ATTACHMENT' && !element.config?.mediaId && (
            <div className="FormBuilderV2__inlineNotice">Choose a file so respondents can open or download it.</div>
          )}
          <div className="FormBuilderV2__elementMetaRow">
            <div className="FormBuilderV2__badges">
              <span>{meta.label}</span>
              {element.required && <span className="FormBuilderV2__badge--required">Required</span>}
              {optionCount > 0 && <span>{optionCount} options</span>}
              {element.config?.allowOther && <span>Other enabled</span>}
              {element.visibility?.conditions?.length > 0 && (
                <span>
                  {element.visibilityEffect === 'HIDE' ? 'Hide' : 'Show'} when {element.visibility.conditions.length}{' '}
                  condition{element.visibility.conditions.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="FormBuilderV2__elementActions" aria-label="Element actions">
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation();
                  duplicateElement(element.id);
                }}
                title="Duplicate element"
                aria-label={`Duplicate ${element.title || meta.label}`}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
              <button
                type="button"
                className="FormBuilderV2__elementDelete"
                onClick={event => {
                  event.stopPropagation();
                  removeElement(element.id);
                }}
                title="Delete element"
                aria-label={`Delete ${element.title || meta.label}`}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          </div>
        </div>
        <div className="FormBuilderV2__moveActions">
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              moveElement(element.id, -1);
            }}
            disabled={index === 0}
            aria-label={`Move ${element.title || meta.label} up`}
          >
            ↑
          </button>
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              moveElement(element.id, 1);
            }}
            disabled={index === definition.elements.length - 1}
            aria-label={`Move ${element.title || meta.label} down`}
          >
            ↓
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`FormBuilderV2${isCompact ? ' FormBuilderV2--compact' : ''}`}>
      {!isCompact && (
        <aside className="FormBuilderV2__palette" aria-label="Form element palette">
          <h4>Add element</h4>
          {PALETTE_GROUPS.map(group => {
            const types = group.types.filter(type => type !== 'ATTACHMENT' || !!uploadFile);
            if (!types.length) return null;
            return (
              <div className="FormBuilderV2__paletteGroup" key={group.label}>
                <div className="FormBuilderV2__paletteGroupTitle">{group.label}</div>
                <div className="FormBuilderV2__paletteGroupDescription">{group.description}</div>
                <div className="FormBuilderV2__paletteList">
                  {types.map(type => (
                    <button type="button" key={type} onClick={() => addElement(type)}>
                      <span>{FORM_FIELD_TYPE_REGISTRY[type].label}</span>
                      <span>+</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </aside>
      )}

      <main
        ref={canvasRef}
        className="FormBuilderV2__canvas"
        style={
          {
            '--form-builder-accent-color': themeColors.accentColor,
            '--form-builder-accent-soft-color': themeColors.accentSoftColor,
            '--form-builder-accent-shadow-color': themeColors.accentShadowColor,
            '--form-builder-page-background-color': themeColors.pageBackgroundColor
          } as React.CSSProperties
        }
      >
        <div className="FormBuilderV2__formHeader">
          <input
            className="form-control"
            aria-label="Form title"
            value={definition.title}
            placeholder="Form title"
            onChange={event => onChange({ ...definition, title: event.target.value })}
          />
          <textarea
            className="form-control"
            aria-label="Form description"
            rows={2}
            value={definition.description}
            placeholder="Form description or instructions"
            onChange={event => onChange({ ...definition, description: event.target.value })}
          />
          {formHeaderExtra}
        </div>

        {definition.elements.length === 0 ? (
          <div className="FormBuilderV2__empty">Add the first question or content element from the left panel.</div>
        ) : (
          <Sortable list={definition.elements} setList={setElements} handle=".FormBuilderV2__drag" animation={150}>
            {definition.elements.map((element, index) => (
              <React.Fragment key={element.id}>{renderElementSummary(element, index)}</React.Fragment>
            ))}
          </Sortable>
        )}
      </main>

      {!isCompact && (
        <aside className="FormBuilderV2__settings" aria-label="Selected element settings">
          <div className="FormBuilderV2__settingsTabs" role="tablist" aria-label="Form settings">
            <button
              type="button"
              className={settingsTab === 'ELEMENT' ? 'is-active' : ''}
              onClick={() => setSettingsTab('ELEMENT')}
            >
              Element
            </button>
            <button
              type="button"
              className={settingsTab === 'APPEARANCE' ? 'is-active' : ''}
              onClick={() => setSettingsTab('APPEARANCE')}
            >
              Form appearance
            </button>
          </div>
          {settingsTab === 'APPEARANCE' ? (
            <div className="FormBuilderV2__appearanceSettings">
              <div className="FormBuilderV2__appearanceRow">
                <label>
                  <span>Theme colour</span>
                  <span className="FormBuilderV2__appearanceSwatchRow">
                    <input
                      type="color"
                      value={themeColors.accentColor}
                      onChange={event => updateTheme({ accentColor: event.target.value, backgroundColor: undefined })}
                    />
                    <small>Applied to buttons, progress bar and highlights.</small>
                  </span>
                </label>
                <div className="FormBuilderV2__themePreview">
                  <span>Auto background</span>
                  <span className="FormBuilderV2__appearanceSwatchRow">
                    <i style={{ backgroundColor: themeColors.pageBackgroundColor }} />
                    <small>Derived automatically from the theme colour.</small>
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => onChange({ ...definition, theme: undefined })}
              >
                Reset style
              </button>
            </div>
          ) : !selected ? (
            <div className="text-muted">Select an element to edit its settings.</div>
          ) : (
            <>
              {!['DIVIDER', 'PARAGRAPH'].includes(selected.type) && (
                <label className="FormBuilderV2__mainField">
                  <span>Title</span>
                  <textarea
                    ref={titleInputRef}
                    className="form-control"
                    rows={3}
                    value={selected.title || ''}
                    onFocus={() => setEmojiTarget('title')}
                    onChange={event => updateSelected({ title: event.target.value })}
                  />
                </label>
              )}

              {!['DIVIDER', 'IMAGE'].includes(selected.type) && (
                <label className="FormBuilderV2__mainField">
                  <span>{selected.type === 'PARAGRAPH' ? 'Paragraph text' : 'Description'}</span>
                  <textarea
                    ref={descriptionInputRef}
                    className="form-control"
                    rows={selected.type === 'PARAGRAPH' ? 6 : 3}
                    value={selected.description || ''}
                    placeholder={selected.type === 'PARAGRAPH' ? 'Add explanatory text shown in the form' : undefined}
                    onFocus={() => setEmojiTarget('description')}
                    onChange={event => updateSelected({ description: event.target.value })}
                  />
                </label>
              )}

              {selected.kind === 'QUESTION' && (
                <>
                  <label className="FormBuilderV2__check">
                    <input
                      type="checkbox"
                      checked={!!selected.required}
                      onChange={event => updateSelected({ required: event.target.checked })}
                    />
                    <span>Required</span>
                  </label>

                  {['SHORT_TEXT', 'LONG_TEXT', 'EMAIL', 'PHONE'].includes(selected.type) && (
                    <>
                      <label>
                        <span>Placeholder</span>
                        <input
                          className="form-control"
                          value={selected.config?.placeholder || ''}
                          onChange={event => updateConfig({ placeholder: event.target.value })}
                        />
                      </label>
                      <label>
                        <span>Maximum characters</span>
                        <input
                          className="form-control"
                          type="number"
                          min={1}
                          value={selected.config?.maxLength || ''}
                          onChange={event =>
                            updateConfig({ maxLength: event.target.value ? Number(event.target.value) : undefined })
                          }
                        />
                      </label>
                    </>
                  )}

                  {['NUMBER', 'RATING'].includes(selected.type) && (
                    <div className="FormBuilderV2__numberGrid">
                      {(['min', 'max', 'step'] as const).map(key => (
                        <label key={key}>
                          <span>{key[0].toUpperCase() + key.slice(1)}</span>
                          <input
                            className="form-control"
                            type="number"
                            value={selected.config?.[key] ?? ''}
                            onChange={event =>
                              updateConfig({ [key]: event.target.value ? Number(event.target.value) : undefined })
                            }
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}

              {FORM_FIELD_TYPE_REGISTRY[selected.type].supportsOptions && (
                <div className="FormBuilderV2__options">
                  <div className="FormBuilderV2__sectionTitle">Options</div>
                  {(selected.config?.options || []).map((option, index) => (
                    <div className="FormBuilderV2__option" key={option.id}>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={option.label}
                        placeholder={`Option ${index + 1}`}
                        onChange={event => updateOption(option.id, event.target.value)}
                      />
                      <button type="button" onClick={() => removeOption(option.id)} aria-label="Remove option">
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      updateConfig({
                        options: [
                          ...(selected.config?.options || []),
                          makeOption(`Option ${(selected.config?.options?.length || 0) + 1}`)
                        ]
                      })
                    }
                  >
                    Add option
                  </button>

                  <label className="FormBuilderV2__check">
                    <input
                      type="checkbox"
                      checked={!!selected.config?.allowOther}
                      onChange={event => updateConfig({ allowOther: event.target.checked })}
                    />
                    <span>Allow “Other” answer</span>
                  </label>

                  {selected.config?.allowOther && (
                    <>
                      <label>
                        <span>Other label</span>
                        <input
                          className="form-control"
                          value={selected.config.otherLabel || 'Other'}
                          onChange={event => updateConfig({ otherLabel: event.target.value })}
                        />
                      </label>
                      <label className="FormBuilderV2__check">
                        <input
                          type="checkbox"
                          checked={!!selected.config.otherRequired}
                          onChange={event => updateConfig({ otherRequired: event.target.checked })}
                        />
                        <span>Require Other details</span>
                      </label>
                    </>
                  )}
                </div>
              )}

              {['HEADING', 'PARAGRAPH', 'SECTION', 'URL'].includes(selected.type) && (
                <div className="FormBuilderV2__contentStyle">
                  <div className="FormBuilderV2__sectionTitle">Text style</div>
                  <label>
                    <span>Insert emoji</span>
                    <div className="FormBuilderV2__emojiPicker" role="group" aria-label="Emoji picker">
                      {EMOJI_PICKER_OPTIONS.map(emoji => (
                        <button
                          type="button"
                          key={emoji}
                          className="FormBuilderV2__emojiPickerItem"
                          onClick={() => insertEmoji(emoji)}
                          aria-label={`Use ${emoji}`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </label>
                  <div className="FormBuilderV2__styleGrid">
                    <label>
                      <span>Text colour</span>
                      <input
                        type="color"
                        value={selected.config?.textColor || '#101828'}
                        onChange={event => updateConfig({ textColor: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Background</span>
                      <input
                        type="color"
                        value={selected.config?.backgroundColor || '#ffffff'}
                        onChange={event => updateConfig({ backgroundColor: event.target.value })}
                      />
                    </label>
                  </div>
                  <label>
                    <span>Align</span>
                    <select
                      className="form-control"
                      value={selected.config?.textAlign || 'left'}
                      onChange={event => updateConfig({ textAlign: event.target.value })}
                    >
                      <option value="left">Left</option>
                      <option value="center">Centre</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                  <label>
                    <span>Size</span>
                    <select
                      className="form-control"
                      value={selected.config?.textSize || 'normal'}
                      onChange={event => updateConfig({ textSize: event.target.value })}
                    >
                      <option value="normal">Normal</option>
                      <option value="large">Large</option>
                      <option value="small">Small</option>
                    </select>
                  </label>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      updateConfig({
                        textColor: undefined,
                        backgroundColor: undefined,
                        textAlign: undefined,
                        textSize: undefined
                      })
                    }
                  >
                    Reset text style
                  </button>
                </div>
              )}

              {selected.type === 'URL' && (
                <div className="FormBuilderV2__contentStyle">
                  <div className="FormBuilderV2__sectionTitle">Link</div>
                  <label>
                    <span>Destination URL</span>
                    <input
                      className="form-control"
                      type="url"
                      placeholder="https://example.com/instructions"
                      value={selected.config?.linkUrl || ''}
                      onChange={event => updateConfig({ linkUrl: event.target.value })}
                    />
                  </label>
                  <div className="text-muted small">
                    The title is shown as the clickable link. The description appears below it.
                  </div>
                </div>
              )}

              {selected.type === 'IMAGE' && (
                <div>
                  <label>
                    <span>Image</span>
                    <input
                      className="form-control-file"
                      type="file"
                      accept="image/*"
                      disabled={!uploadImage || isUploadingMedia}
                      onChange={async event => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file || !uploadImage) return;
                        setMediaError('');
                        const previewUrl = URL.createObjectURL(file);
                        setLocalImagePreviews(prev => {
                          const oldUrl = prev[selected.id];
                          if (oldUrl) URL.revokeObjectURL(oldUrl);
                          return { ...prev, [selected.id]: previewUrl };
                        });
                        setIsUploadingMedia(true);
                        try {
                          const uploaded = await uploadImage(file);
                          updateConfig(
                            typeof uploaded === 'string'
                              ? { mediaId: undefined, mediaKey: undefined, mediaToken: undefined, mediaUrl: uploaded }
                              : mediaConfigFromUpload(uploaded)
                          );
                        } catch (error) {
                          setLocalImagePreviews(prev => {
                            const oldUrl = prev[selected.id];
                            if (oldUrl) URL.revokeObjectURL(oldUrl);
                            const next = { ...prev };
                            delete next[selected.id];
                            return next;
                          });
                          setMediaError(error instanceof Error ? error.message : 'Unable to upload image');
                        } finally {
                          setIsUploadingMedia(false);
                        }
                      }}
                    />
                  </label>
                  {(selected.config?.mediaId ||
                    selected.config?.mediaKey ||
                    selected.config?.mediaUrl ||
                    localImagePreviews[selected.id]) && (
                    <img
                      src={localImagePreviews[selected.id] || mediaUrlFromConfig(selected)}
                      alt={selected.title || ''}
                      className="FormBuilderV2__imagePreview"
                    />
                  )}
                  {isUploadingMedia && <div className="small text-info">Uploading…</div>}
                  {mediaError && <div className="invalid-feedback d-block">{mediaError}</div>}
                  {!uploadImage && <div className="small text-muted">Image upload is unavailable in this context.</div>}
                </div>
              )}

              {selected.type === 'ATTACHMENT' && (
                <div>
                  <label>
                    <span>File</span>
                    <input
                      className="form-control-file"
                      type="file"
                      disabled={!uploadFile || isUploadingMedia}
                      onChange={async event => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        if (!file || !uploadFile) return;
                        setMediaError('');
                        setIsUploadingMedia(true);
                        try {
                          const uploaded = await uploadFile(file);
                          updateSelected({
                            title:
                              !String(selected.title || '').trim() || selected.title === 'File attachment'
                                ? uploaded.name
                                : selected.title,
                            config: {
                              ...(selected.config || {}),
                              ...mediaConfigFromUpload(uploaded)
                            }
                          });
                        } catch (error) {
                          setMediaError(error instanceof Error ? error.message : 'Unable to upload file');
                        } finally {
                          setIsUploadingMedia(false);
                        }
                      }}
                    />
                  </label>
                  {(selected.config?.mediaId || selected.config?.mediaKey || selected.config?.mediaUrl) && (
                    <div className="alert alert-light border py-2 px-3">
                      <div className="FormBuilderV2__mediaName font-weight-bold">
                        {selected.config.mediaName || selected.title}
                      </div>
                      <div className="small text-muted">
                        {selected.config.mediaMimeType || 'File'}
                        {selected.config.mediaSize ? ` · ${Math.ceil(selected.config.mediaSize / 1024)} KB` : ''}
                      </div>
                    </div>
                  )}
                  {isUploadingMedia && <div className="small text-info">Uploading…</div>}
                  {mediaError && <div className="invalid-feedback d-block">{mediaError}</div>}
                  {!uploadFile && <div className="small text-muted">File upload is unavailable in this context.</div>}
                </div>
              )}

              {selected.type === 'FILE_UPLOAD' && (
                <div>
                  <label>
                    <span>Maximum files</span>
                    <input
                      className="form-control"
                      type="number"
                      min={1}
                      max={10}
                      value={selected.config?.maxFiles || 1}
                      onChange={event => updateConfig({ maxFiles: Math.max(1, Number(event.target.value) || 1) })}
                    />
                  </label>
                  <label>
                    <span>Maximum size per file (MB)</span>
                    <input
                      className="form-control"
                      type="number"
                      min={1}
                      max={50}
                      value={Math.round((selected.config?.maxFileSizeBytes || 10 * 1024 * 1024) / 1024 / 1024)}
                      onChange={event =>
                        updateConfig({ maxFileSizeBytes: Math.max(1, Number(event.target.value) || 1) * 1024 * 1024 })
                      }
                    />
                  </label>
                  <label>
                    <span>Allowed MIME types</span>
                    <input
                      className="form-control"
                      placeholder="image/jpeg, application/pdf"
                      value={(selected.config?.allowedMimeTypes || []).join(', ')}
                      onChange={event =>
                        updateConfig({
                          allowedMimeTypes: event.target.value
                            .split(',')
                            .map(value => value.trim())
                            .filter(Boolean)
                        })
                      }
                    />
                  </label>
                </div>
              )}

              <div className="FormBuilderV2__logic">
                <div className="FormBuilderV2__sectionTitle">Conditional visibility</div>

                {selected.type === 'PAGE_BREAK' ? (
                  <div className="text-muted small">Page routing is configured in the Page flow section below.</div>
                ) : sourceQuestions.length === 0 ? (
                  <div className="text-muted small">Add another question before configuring conditions.</div>
                ) : (
                  <>
                    <label className="FormBuilderV2__check">
                      <input
                        type="checkbox"
                        checked={!!selected.visibility?.conditions?.length}
                        onChange={event => setVisibilityEnabled(event.target.checked)}
                      />
                      <span>Use conditional visibility</span>
                    </label>

                    {selected.visibility?.conditions?.length > 0 && (
                      <>
                        <div className="FormBuilderV2__logicHeader">
                          <select
                            className="form-control"
                            value={selected.visibilityEffect || 'SHOW'}
                            onChange={event =>
                              updateSelected({ visibilityEffect: event.target.value as 'SHOW' | 'HIDE' })
                            }
                          >
                            <option value="SHOW">Show this element when</option>
                            <option value="HIDE">Hide this element when</option>
                          </select>
                          <select
                            className="form-control"
                            value={selected.visibility.match}
                            onChange={event =>
                              updateSelected({
                                visibility: {
                                  ...selected.visibility!,
                                  match: event.target.value as 'ALL' | 'ANY'
                                }
                              })
                            }
                          >
                            <option value="ALL">All conditions match</option>
                            <option value="ANY">Any condition matches</option>
                          </select>
                        </div>

                        {selected.visibility.conditions.map((condition, index) => {
                          const source = definition.elements.find(element => element.id === condition.fieldId);
                          const operators = operatorsForElement(source);

                          return (
                            <div className="FormBuilderV2__condition" key={`${selected.id}_condition_${index}`}>
                              <select
                                className="form-control"
                                value={condition.fieldId}
                                onChange={event => changeConditionSource(index, event.target.value)}
                              >
                                {sourceQuestions.map(question => (
                                  <option key={question.id} value={question.id}>
                                    {question.title || FORM_FIELD_TYPE_REGISTRY[question.type].label}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="form-control"
                                value={condition.operator}
                                onChange={event => {
                                  const operator = event.target.value as FormConditionOperator;
                                  updateCondition(index, {
                                    operator,
                                    value: EMPTY_OPERATORS.includes(operator)
                                      ? undefined
                                      : EMPTY_OPERATORS.includes(condition.operator)
                                      ? defaultConditionValue(source)
                                      : condition.value
                                  });
                                }}
                              >
                                {operators.map(operator => (
                                  <option key={operator} value={operator}>
                                    {OPERATOR_LABELS[operator]}
                                  </option>
                                ))}
                              </select>
                              {renderConditionValue(condition, patch => updateCondition(index, patch))}
                              <button
                                type="button"
                                className="FormBuilderV2__conditionRemove"
                                onClick={() => removeCondition(index)}
                                aria-label="Remove condition"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}

                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addCondition}>
                          Add condition
                        </button>
                      </>
                    )}
                  </>
                )}

                {selectedIssues.length > 0 && (
                  <div className="alert alert-warning mb-0" role="alert" aria-live="polite">
                    {selectedIssues.map((issue, index) => (
                      <div key={`${issue.message}_${index}`}>{issue.message}</div>
                    ))}
                  </div>
                )}
              </div>

              <div className="FormBuilderV2__flowRules">
                <div className="FormBuilderV2__settingsHeader">
                  <div>
                    <div className="FormBuilderV2__sectionTitle">Page flow</div>
                    <div className="text-muted small">The first matching rule is used when the user clicks Next.</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm FormBuilderV2__addRule"
                    onClick={addLogicRule}
                  >
                    Add rule
                  </button>
                </div>

                {(definition.logic || []).filter(rule =>
                  ['GO_TO_PAGE', 'SUBMIT', 'END_WITH_MESSAGE'].includes(rule.action.type)
                ).length === 0 && (
                  <div className="text-muted small">No branching rules. Pages continue in their displayed order.</div>
                )}

                {(definition.logic || [])
                  .filter(rule => ['GO_TO_PAGE', 'SUBMIT', 'END_WITH_MESSAGE'].includes(rule.action.type))
                  .map((rule, ruleIndex) => (
                    <div className="FormBuilderV2__flowRule" key={rule.id}>
                      <div className="FormBuilderV2__flowRuleTitle">
                        <strong>Rule {ruleIndex + 1}</strong>
                        <button type="button" onClick={() => removeLogicRule(rule.id)} aria-label="Remove rule">
                          ×
                        </button>
                      </div>

                      <div className="FormBuilderV2__logicHeader">
                        <label>
                          <span>From page</span>
                          <select
                            className="form-control"
                            value={rule.sourcePageId}
                            onChange={event => {
                              const sourcePageId = event.target.value;
                              const availableQuestions = questionsForSourcePage(sourcePageId);
                              const fallbackQuestion = availableQuestions[0];
                              const nextWhen = {
                                ...rule.when,
                                conditions: rule.when.conditions.map(condition => {
                                  if (availableQuestions.some(question => question.id === condition.fieldId)) {
                                    return condition;
                                  }
                                  return fallbackQuestion
                                    ? {
                                        fieldId: fallbackQuestion.id,
                                        operator: operatorsForElement(fallbackQuestion)[0],
                                        value: defaultConditionValue(fallbackQuestion)
                                      }
                                    : condition;
                                })
                              };
                              const availableTargets = targetsAfterPage(sourcePageId);
                              const currentTargetId =
                                rule.action.type === 'GO_TO_PAGE' ? rule.action.targetId : undefined;
                              const nextAction =
                                rule.action.type === 'GO_TO_PAGE'
                                  ? availableTargets.some(page => page.id === currentTargetId)
                                    ? rule.action
                                    : availableTargets[0]
                                    ? { type: 'GO_TO_PAGE' as const, targetId: availableTargets[0].id }
                                    : { type: 'SUBMIT' as const }
                                  : rule.action;
                              updateLogicRule(rule.id, { sourcePageId, when: nextWhen, action: nextAction });
                            }}
                          >
                            {pages.map((page, pageIndex) => (
                              <option key={page.id} value={page.id}>
                                {pageIndex + 1}. {page.title}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Match</span>
                          <select
                            className="form-control"
                            value={rule.when.match}
                            onChange={event =>
                              updateLogicRule(rule.id, {
                                when: { ...rule.when, match: event.target.value as 'ALL' | 'ANY' }
                              })
                            }
                          >
                            <option value="ALL">All conditions</option>
                            <option value="ANY">Any condition</option>
                          </select>
                        </label>
                      </div>

                      {rule.when.conditions.map((condition, conditionIndex) => {
                        const source = definition.elements.find(element => element.id === condition.fieldId);
                        return (
                          <div className="FormBuilderV2__condition" key={`${rule.id}_${conditionIndex}`}>
                            <select
                              className="form-control"
                              value={condition.fieldId}
                              onChange={event => {
                                const nextSource = definition.elements.find(
                                  element => element.id === event.target.value
                                );
                                updateLogicCondition(rule.id, conditionIndex, {
                                  fieldId: event.target.value,
                                  operator: operatorsForElement(nextSource)[0],
                                  value: defaultConditionValue(nextSource)
                                });
                              }}
                            >
                              {questionsForSourcePage(rule.sourcePageId).map(question => (
                                <option key={question.id} value={question.id}>
                                  {question.title || FORM_FIELD_TYPE_REGISTRY[question.type].label}
                                </option>
                              ))}
                            </select>
                            <select
                              className="form-control"
                              value={condition.operator}
                              onChange={event => {
                                const operator = event.target.value as FormConditionOperator;
                                updateLogicCondition(rule.id, conditionIndex, {
                                  operator,
                                  value: EMPTY_OPERATORS.includes(operator)
                                    ? undefined
                                    : EMPTY_OPERATORS.includes(condition.operator)
                                    ? defaultConditionValue(source)
                                    : condition.value
                                });
                              }}
                            >
                              {operatorsForElement(source).map(operator => (
                                <option key={operator} value={operator}>
                                  {OPERATOR_LABELS[operator]}
                                </option>
                              ))}
                            </select>
                            {renderConditionValue(condition, patch =>
                              updateLogicCondition(rule.id, conditionIndex, patch)
                            )}
                            <button
                              type="button"
                              className="FormBuilderV2__conditionRemove"
                              onClick={() => removeLogicCondition(rule, conditionIndex)}
                              aria-label="Remove condition"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}

                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => addLogicCondition(rule)}
                      >
                        Add condition
                      </button>

                      <label>
                        <span>Action</span>
                        <select
                          className="form-control"
                          value={rule.action.type}
                          onChange={event =>
                            setLogicActionType(rule, event.target.value as 'GO_TO_PAGE' | 'SUBMIT' | 'END_WITH_MESSAGE')
                          }
                        >
                          {targetsAfterPage(rule.sourcePageId).length > 0 && (
                            <option value="GO_TO_PAGE">Go to page</option>
                          )}
                          <option value="SUBMIT">Submit immediately</option>
                          <option value="END_WITH_MESSAGE">End with message</option>
                        </select>
                      </label>

                      {rule.action.type === 'GO_TO_PAGE' && (
                        <label>
                          <span>Target page</span>
                          <select
                            className="form-control"
                            value={rule.action.targetId}
                            onChange={event =>
                              updateLogicRule(rule.id, {
                                action: { type: 'GO_TO_PAGE', targetId: event.target.value }
                              })
                            }
                          >
                            {targetsAfterPage(rule.sourcePageId).map((page, pageIndex) => (
                              <option key={page.id} value={page.id}>
                                {pageIndex + 1}. {page.title}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                      {rule.action.type === 'END_WITH_MESSAGE' && (
                        <label>
                          <span>Completion message</span>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={rule.action.message}
                            onChange={event =>
                              updateLogicRule(rule.id, {
                                action: { type: 'END_WITH_MESSAGE', message: event.target.value }
                              })
                            }
                          />
                        </label>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </aside>
      )}
    </div>
  );
};
